// src/components/SessionTracker.js
// Drop this into your App.js layout so it runs on every page.
// It tracks active usage time by sending heartbeats to Firestore.
//
// Usage in App.js:
//   import SessionTracker from './components/SessionTracker';
//   // Inside your authenticated layout:
//   <SessionTracker />

import { useEffect, useRef } from 'react';
import { getFirestore, doc, updateDoc, increment, serverTimestamp, getDoc, setDoc } from 'firebase/firestore';
import { auth } from '../firebaseConfig';

const HEARTBEAT_INTERVAL = 30_000; // 30 seconds
const IDLE_TIMEOUT = 5 * 60_000;   // 5 minutes of no activity → idle

function SessionTracker() {
  const lastActivityRef = useRef(Date.now());
  const intervalRef = useRef(null);
  const isIdleRef = useRef(false);

  useEffect(() => {
    const db = getFirestore();

    // ── Mark user as active on any interaction ──
    const markActive = () => {
      lastActivityRef.current = Date.now();
      if (isIdleRef.current) {
        isIdleRef.current = false;
        console.log('⏱️ Session resumed (user active again)');
      }
    };

    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    activityEvents.forEach(evt => window.addEventListener(evt, markActive, { passive: true }));

    // ── Heartbeat: runs every 30s, only increments if user was active ──
    const sendHeartbeat = async () => {
      const user = auth.currentUser;
      if (!user) return;

      const now = Date.now();
      const timeSinceActivity = now - lastActivityRef.current;

      // If idle for too long, skip
      if (timeSinceActivity > IDLE_TIMEOUT) {
        if (!isIdleRef.current) {
          isIdleRef.current = true;
          console.log('⏱️ User idle — pausing session tracking');
        }
        return;
      }

      try {
        const userRef = doc(db, 'users', user.uid);
        const today = new Date().toISOString().split('T')[0]; // "2026-03-03"

        // Check if we need to reset today's counter
        const userSnap = await getDoc(userRef);
        const userData = userSnap.exists() ? userSnap.data() : {};

        const updates = {
          totalSessionSeconds: increment(30),
          lastHeartbeat: serverTimestamp(),
          lastLogin: new Date().toISOString(),
        };

        // Reset daily counter if it's a new day
        if (userData.sessionDate !== today) {
          updates.todaySessionSeconds = 30;
          updates.sessionDate = today;
        } else {
          updates.todaySessionSeconds = increment(30);
        }

        await updateDoc(userRef, updates);
      } catch (err) {
        // Silently fail — don't disrupt the user experience
        if (err.code !== 'not-found') {
          console.warn('⏱️ Heartbeat error:', err.message);
        }
      }
    };

    // Start heartbeat loop
    intervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);

    // Send an initial heartbeat on mount
    sendHeartbeat();

    // ── Cleanup ──
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      activityEvents.forEach(evt => window.removeEventListener(evt, markActive));
    };
  }, []);

  // This component renders nothing
  return null;
}

export default SessionTracker;
